import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql } from '@apollo/client';
import {
  PageHeaderStyles,
  Title,
  Subtitle,
  HealthBar,
  HealthStats,
  HealthStat,
  HealthLabel,
  HealthValue,
  HealthWarning,
  Tabs,
  TabDescription,
  FilterGroup,
  FilterLabel,
  DocumentsGrid,
  DocumentHeaderRow,
  DocumentTitle,
  DocumentId,
  DocumentName,
  StatusRow,
  DocumentStatus,
  ImpactBadge,
  DocumentMeta,
  MetaRow,
  MetaLabel,
  MetaValue,
  DocumentActivity,
  ImpactSignal,
  DocumentActions,
  EmptyState,
  ChangeIndicator,
  BlockingToggle,
} from './styles';
import { calculateHealthStats } from './healthStats';
import { Document } from './types';
import { Button, Checkbox, SearchableSelect, Select, CardContainer, TabsContainer as TabsContainerComponent, FilterBar as FilterBarComponent, PaginationControls, usePagination } from '@tviklax/ui-components';
import { useGraphQLQuery } from '@tviklax/ui-components';
import { showToast } from '../../utils/toastUtils';
import PeoplePanel from './PeoplePanel';
import { DocumentDetails } from './DocumentDetails';
import { API_BASE_URL } from '../../shared/constants/env';
import { useUser } from '../../context/UserContext';

type TabType = 'commercial' | 'customs';

const SUPPLIER_CONTACTS_QUERY = gql`
  query DocumentsSupplierContacts($customerId: Int) {
    myOrders(customerId: $customerId, limit: 100, offset: 0) {
      orderId
      vendorId
    }
    myTradePartnerContacts(limit: 100) {
      items {
        id
        name
        email
        phone
        role
        organization
        partnerId
      }
    }
  }
`;

interface SupplierOrderRef {
  orderId: string;
  vendorId?: number | null;
}

interface SupplierTradePartnerContact {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  organization?: string | null;
  partnerId?: number | null;
}

const GENERAL_DOCUMENTS_QUERY = gql`
  query DocumentsGeneral($limit: Int!, $offset: Int!) {
    documents(limit: $limit, offset: $offset) {
      id
      documentId
      orderId
      documentName
      category
      documentType
      status
      fileUrl
      owner
      dueDate
      impact
      authorityRemark
      uploadedDate
      lastActionTime
      forexComplianceId
    }
  }
`;

const FOREX_DOCUMENTS_QUERY = gql`
  query DocumentsForexComplianceRecords($limit: Int!, $offset: Int!) {
    forexComplianceRecords(limit: $limit, offset: $offset) {
      id
      orderId
      supplier
      checklist {
        requirement
        status
        file
        notes
        required
        owner
        requestedOn
        uploadedOn
        approvedOn
        documentId
        orderId
      }
      contacts {
        id
        name
        email
        role
        team
        organization
        visibility
        assignmentRole
      }
    }
  }
`;

interface ForexChecklistItem {
  requirement: string;
  status: 'pending' | 'uploaded' | 'approved' | 'rejected';
  file?: string | null;
  notes?: string | null;
  required: boolean;
  owner: 'supplier' | 'finance' | 'ops' | 'agent';
  requestedOn?: string | null;
  uploadedOn?: string | null;
  approvedOn?: string | null;
  documentId?: string | null;
  orderId?: number | null;
}

interface GeneralDocument {
  id: number;
  documentId?: string | null;
  orderId?: number | null;
  documentName?: string | null;
  category?: string | null;
  documentType?: string | null;
  status?: string | null;
  fileUrl?: string | null;
  owner?: string | null;
  dueDate?: string | null;
  impact?: string | null;
  authorityRemark?: string | null;
  uploadedDate?: string | null;
  lastActionTime?: string | null;
  forexComplianceId?: number | null;
}

interface ForexDocumentRecord {
  id: string;
  orderId?: number | null;
  supplier?: string | null;
  checklist: ForexChecklistItem[];
  contacts: Array<{
    id: number;
    name: string;
    email?: string;
    role?: string;
    team?: string;
    organization?: string;
    country?: string;
    visibility?: string;
    assignmentRole?: string;
  }>;
}

interface DocumentsPageProps {
  onOpenOrder?: (orderId: string) => void;
  onOpenMessaging?: (payload: { targetContactName?: string; targetContactEmail?: string; preferredSurface?: 'drawer' | 'widget' }) => void;
  initialDocumentId?: string;
}

const getApiOrigin = (): string => API_BASE_URL.replace(/\/graphql\/?$/, '');

const buildGeneratedDocumentUrl = (relativePath: string): string => {
  const cleanRelative = relativePath
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${getApiOrigin()}/generated-documents/${cleanRelative}`;
};

const resolveDocumentUrl = (filePath?: string): string | null => {
  if (!filePath) return null;
  const normalizedPath = filePath.trim();
  if (!normalizedPath) return null;
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;

  if (normalizedPath.startsWith('/generated-documents/')) {
    return `${getApiOrigin()}${normalizedPath}`;
  }

  if (normalizedPath.startsWith('generated_documents/')) {
    return buildGeneratedDocumentUrl(normalizedPath.replace(/^generated_documents\//, ''));
  }

  // Handle legacy absolute filesystem paths that contain generated_documents sub-paths.
  const genDocsMarker = 'generated_documents/';
  const genDocsIdx = normalizedPath.indexOf(genDocsMarker);
  if (genDocsIdx !== -1) {
    return buildGeneratedDocumentUrl(normalizedPath.slice(genDocsIdx + genDocsMarker.length));
  }

  if (!normalizedPath.includes('/') && !normalizedPath.includes('\\') && normalizedPath.includes('.')) {
    const lowerName = normalizedPath.toLowerCase();
    if (lowerName.startsWith('po-')) {
      return buildGeneratedDocumentUrl(`purchase_orders/${normalizedPath}`);
    }
    return buildGeneratedDocumentUrl(`forex_uploads/${normalizedPath}`);
  }

  return normalizedPath;
};

const getDownloadFileName = (url: string, fallbackName: string): string => {
  const clean = url.split('?')[0].split('#')[0];
  const candidate = clean.split('/').filter(Boolean).pop();
  if (!candidate) return fallbackName;
  try {
    return decodeURIComponent(candidate);
  } catch {
    return candidate;
  }
};

const buildForcedDownloadUrl = (fileUrl: string): string => {
  return `${getApiOrigin()}/api/v1/forex/download-document?fileUrl=${encodeURIComponent(fileUrl)}`;
};

const mapChecklistStatusToDocumentStatus = (status: ForexChecklistItem['status'], required: boolean): Document['status'] => {
  if (status === 'approved') return 'cleared';
  if (status === 'rejected' && required) return 'blocking';
  return 'pending';
};

const mapRequirementToCategory = (requirement: string): Document['category'] => {
  const normalized = requirement.trim().toLowerCase();
  if (normalized.includes('hs code') || normalized.includes('fema') || normalized.includes('kyc') || normalized.includes('declaration')) {
    return 'customs';
  }
  return 'commercial';
};

const mapOwnerToIssuedBy = (owner: string | null | undefined, supplierName?: string | null): string => {
  if (!owner) return 'System';
  const label = owner.charAt(0).toUpperCase() + owner.slice(1);
  if (owner === 'supplier' && supplierName && supplierName.trim() && supplierName !== 'Unassigned Supplier') {
    return `${label} (${supplierName.trim()})`;
  }
  return label;
};

const getContactForOwner = (owner: ForexChecklistItem['owner'], record: ForexDocumentRecord) => {
  // Map checklist owner to assignment_role
  const roleMap: Record<string, string> = {
    'ops': 'ops_owner',
    'finance': 'finance_contact',
    'supplier': 'customer',
    'agent': 'customs_broker',
  };
  
  const assignmentRole = roleMap[owner];
  if (!assignmentRole) return null;
  
  const contact = (record.contacts || []).find(c => c.assignmentRole === assignmentRole);
  if (!contact) return null;

  return {
    id: contact.id,
    name: contact.name,
    email: contact.email,
    phone: undefined as string | undefined,
    organization: contact.organization,
    role: contact.role,
    team: contact.team,
    country: contact.country,
  };
};

const mapRecordToDocuments = (
  record: ForexDocumentRecord,
  supplierContact?: SupplierTradePartnerContact
): Document[] => {
  return (record.checklist || []).map((item, index) => {
    const status = mapChecklistStatusToDocumentStatus(item.status, item.required);
    // Real, verified contact details (email/phone) only exist for the
    // supplier side today — see myTradePartnerContacts, which is scoped to
    // contacts the supplier has explicitly marked visible to trade
    // partners. Finance/ops are Tvikla's own internal team; the compliance
    // checklist's contacts join is never populated for them (and showing a
    // specific staffer's phone number to a customer isn't the intent), so
    // those stay as generic owner labels rather than named contacts.
    const contactInfo = item.owner === 'supplier'
      ? (supplierContact
        ? {
          id: supplierContact.id,
          name: supplierContact.name,
          email: supplierContact.email ?? undefined,
          phone: supplierContact.phone ?? undefined,
          role: supplierContact.role ?? undefined,
          organization: supplierContact.organization ?? undefined,
          team: undefined as string | undefined,
          country: undefined as string | undefined,
        }
        : undefined)
      : getContactForOwner(item.owner, record);
    return {
      id: item.documentId || `DOC-${record.id}-${index + 1}`,
      name: item.requirement,
      fileUrl: resolveDocumentUrl(item.file || undefined) || undefined,
      category: mapRequirementToCategory(item.requirement),
      status,
      impact: item.required && status !== 'cleared' ? 'blocks-customs' : 'non-blocking',
      orderId: record.id,
      issuedBy: mapOwnerToIssuedBy(item.owner, record.supplier),
      submittedAt: item.requestedOn || item.uploadedOn || item.approvedOn || new Date().toISOString(),
      contactId: contactInfo?.id,
      contactTeam: contactInfo?.team,
      handledBy: contactInfo?.name,
      handledByEmail: contactInfo?.email,
      handledByPhone: contactInfo?.phone,
      handledByOrganization: contactInfo?.organization,
      handledByRole: contactInfo?.role,
      handledByCountry: contactInfo?.country,
      owner: item.owner,
      lastAction: item.notes || `Checklist status: ${item.status}`,
      lastActionTime: item.approvedOn || item.uploadedOn || item.requestedOn || new Date().toISOString(),
      version: 1,
      dueDate: item.required && status !== 'cleared' ? item.requestedOn || undefined : undefined,
      blockedEntities: item.required && status !== 'cleared' ? [record.id] : undefined,
      changeIndicator: item.uploadedOn ? 'updated-today' : 'no-change',
      authorityRemark: item.notes || undefined,
    };
  });
};

// status and impact are independent columns on the same row — a document
// can carry impact='blocks-delivery' without status ever being set to
// 'blocking'/'rejected' (and vice versa). Checking status alone here used
// to disagree with the dashboard, which checks impact alone — a doc could
// show "Blocks delivery" there and "Non-blocking" here for the same row.
const mapDocumentStatusToPageStatus = (status?: string | null, impact?: string | null): Document['status'] => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'blocking' || normalized === 'rejected' || impact === 'blocks-delivery') return 'blocking';
  if (normalized === 'approved') return 'cleared';
  return 'pending';
};

// documents rows that were created directly (not through the forex compliance
// checklist, so forexComplianceId is null) — e.g. commercial invoices, packing
// lists. orderRefs maps the numeric order id to its display ref (e.g. "ORD-001"),
// built from whatever forexComplianceRecords already fetched.
const mapGeneralDocumentsToDocuments = (
  docs: GeneralDocument[],
  orderRefs: Map<number, string>,
  supplierByOrderId: Map<number, string>
): Document[] => {
  return docs
    .filter((doc) => doc.forexComplianceId === null || doc.forexComplianceId === undefined)
    .map((doc) => {
      const status = mapDocumentStatusToPageStatus(doc.status, doc.impact);
      const orderRef = (doc.orderId !== null && doc.orderId !== undefined && orderRefs.get(doc.orderId)) || `ORD-${doc.orderId ?? doc.id}`;
      const supplierName = doc.orderId !== null && doc.orderId !== undefined ? supplierByOrderId.get(doc.orderId) : undefined;
      return {
        id: doc.documentId || `DOC-${doc.id}`,
        name: doc.documentName || doc.documentType || 'Document',
        fileUrl: resolveDocumentUrl(doc.fileUrl || undefined) || undefined,
        category: doc.category === 'customs' ? 'customs' : 'commercial',
        status,
        impact: (doc.impact as Document['impact']) || 'non-blocking',
        orderId: orderRef,
        issuedBy: mapOwnerToIssuedBy(doc.owner, supplierName),
        submittedAt: doc.uploadedDate || doc.dueDate || new Date().toISOString(),
        owner: doc.owner || undefined,
        lastAction: doc.authorityRemark || `Status: ${doc.status}`,
        lastActionTime: doc.lastActionTime || doc.uploadedDate || new Date().toISOString(),
        version: 1,
        dueDate: doc.dueDate || undefined,
        blockedEntities: status === 'blocking' ? [orderRef] : undefined,
        changeIndicator: doc.uploadedDate ? 'updated-today' : 'no-change',
        authorityRemark: doc.authorityRemark || undefined,
      };
    });
};

const DocumentsPage: React.FC<DocumentsPageProps> = ({ onOpenOrder, onOpenMessaging, initialDocumentId }) => {
  const [activeTab, setActiveTab] = useState<TabType>('commercial');
  const [selectedOrder, setSelectedOrder] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedTimeRisk, setSelectedTimeRisk] = useState<string>('all');
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [selectedDocumentForDetails, setSelectedDocumentForDetails] = useState<Document | null>(null);
  const { data: documentsData } = useGraphQLQuery<{ forexComplianceRecords: ForexDocumentRecord[] }>(FOREX_DOCUMENTS_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
    pollInterval: 4000,
  });
  const { data: generalDocumentsData } = useGraphQLQuery<{ documents: GeneralDocument[] }>(GENERAL_DOCUMENTS_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
    pollInterval: 4000,
  });
  const { user } = useUser();
  const customerId = user?.customerId;
  const { data: supplierContactsData } = useGraphQLQuery<{
    myOrders: SupplierOrderRef[];
    myTradePartnerContacts: { items: SupplierTradePartnerContact[] };
  }>(SUPPLIER_CONTACTS_QUERY, {
    variables: { customerId: customerId ?? null },
    showErrorToast: false,
    skip: customerId === undefined,
  });
  const headerRef = useRef<HTMLDivElement>(null);
  const [peoplePanelOffset, setPeoplePanelOffset] = useState<number>(140);

  useEffect(() => {
    const updatePanelOffset = () => {
      if (!headerRef.current) return;
      const rect = headerRef.current.getBoundingClientRect();
      const gapBelowHeader = 12;
      const nextOffset = Math.max(rect.top + rect.height + gapBelowHeader, 120);
      setPeoplePanelOffset(nextOffset);
    };

    updatePanelOffset();
    window.addEventListener('resize', updatePanelOffset);
    return () => window.removeEventListener('resize', updatePanelOffset);
  }, []);

  const orderRefs = useMemo(() => {
    const lookup = new Map<number, string>();
    (documentsData?.forexComplianceRecords || []).forEach((record) => {
      if (record.orderId !== null && record.orderId !== undefined) {
        lookup.set(record.orderId, record.id);
      }
    });
    return lookup;
  }, [documentsData?.forexComplianceRecords]);

  const supplierByOrderId = useMemo(() => {
    const lookup = new Map<number, string>();
    (documentsData?.forexComplianceRecords || []).forEach((record) => {
      if (record.orderId !== null && record.orderId !== undefined && record.supplier) {
        lookup.set(record.orderId, record.supplier);
      }
    });
    return lookup;
  }, [documentsData?.forexComplianceRecords]);

  // A vendor can have more than one customer-visible contact — pick the
  // first, there's no "primary" concept on the trade-partner side to prefer
  // (same approach CustomerDashboard uses for "Message supplier").
  const supplierContactByOrderId = useMemo(() => {
    const contactByVendorId = new Map<number, SupplierTradePartnerContact>();
    (supplierContactsData?.myTradePartnerContacts?.items || []).forEach((c) => {
      if (c.partnerId != null && !contactByVendorId.has(c.partnerId)) {
        contactByVendorId.set(c.partnerId, c);
      }
    });
    const lookup = new Map<string, SupplierTradePartnerContact>();
    (supplierContactsData?.myOrders || []).forEach((order) => {
      if (order.vendorId == null) return;
      const contact = contactByVendorId.get(order.vendorId);
      if (contact) lookup.set(order.orderId, contact);
    });
    return lookup;
  }, [supplierContactsData]);

  const allDocuments = useMemo(() => {
    const complianceDocuments = (documentsData?.forexComplianceRecords || []).flatMap((record) =>
      mapRecordToDocuments(record, supplierContactByOrderId.get(record.id))
    );
    const generalDocuments = mapGeneralDocumentsToDocuments(generalDocumentsData?.documents || [], orderRefs, supplierByOrderId);
    return [...generalDocuments, ...complianceDocuments];
  }, [documentsData?.forexComplianceRecords, generalDocumentsData?.documents, orderRefs, supplierByOrderId, supplierContactByOrderId]);

  const openedInitialDocumentRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!initialDocumentId || openedInitialDocumentRef.current === initialDocumentId) return;
    // Wait for both queries so the order ref lookup (built from
    // forexComplianceRecords) is populated before we bake a "Linked to" value
    // into the opened document — otherwise it can fall back to "ORD-{id}"
    // just because the general-documents query resolved first.
    if (!documentsData?.forexComplianceRecords || !generalDocumentsData?.documents) return;
    const match = allDocuments.find((doc) => doc.id === initialDocumentId);
    if (match) {
      openedInitialDocumentRef.current = initialDocumentId;
      setSelectedOrder(match.orderId);
      setActiveTab(match.category === 'customs' ? 'customs' : 'commercial');
      setSelectedDocumentForDetails(match);
    }
  }, [initialDocumentId, allDocuments, documentsData?.forexComplianceRecords, generalDocumentsData?.documents]);

  const healthStats = useMemo(() => calculateHealthStats(allDocuments), [allDocuments]);

  // Filter documents based on tab and filters
  const filteredDocuments = useMemo(() => {
    let docs = allDocuments.filter(doc => doc.category === activeTab);

    if (blockingOnly) {
      docs = docs.filter(doc => doc.status === 'blocking');
    }

    if (selectedOrder !== 'all') {
      docs = docs.filter(doc => doc.orderId === selectedOrder || doc.shipmentId === selectedOrder);
    }

    if (selectedStatus !== 'all') {
      docs = docs.filter(doc => doc.status === selectedStatus);
    }

    if (selectedTimeRisk !== 'all') {
      const now = new Date();
      docs = docs.filter(doc => {
        if (!doc.dueDate) return selectedTimeRisk === 'no-deadline';
        const due = new Date(doc.dueDate);
        const daysUntilDue = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        switch (selectedTimeRisk) {
          case 'overdue':
            return daysUntilDue < 0;
          case 'urgent':
            return daysUntilDue >= 0 && daysUntilDue <= 2;
          case 'normal':
            return daysUntilDue > 2;
          default:
            return true;
        }
      });
    }

    return docs;
  }, [activeTab, allDocuments, selectedOrder, selectedStatus, selectedTimeRisk, blockingOnly]);

  const {
    currentPage,
    pageSize,
    paginatedItems,
    setCurrentPage,
    setPageSize,
  } = usePagination({ items: filteredDocuments, initialPageSize: 12 });

  // Get unique orders and shipments for filter
  const orderOptions = useMemo(() => {
    const orders = new Set<string>();
    allDocuments.forEach(doc => {
      orders.add(doc.orderId);
      if (doc.shipmentId) orders.add(doc.shipmentId);
    });
    return Array.from(orders).sort();
  }, [allDocuments]);

  const getImpactIcon = (impact: Document['impact']) => {
    switch (impact) {
      case 'non-blocking':
        return '🟢';
      case 'blocks-customs':
        return '🔴';
      case 'blocks-delivery':
        return '🟠';
    }
  };

  const getImpactText = (impact: Document['impact']) => {
    switch (impact) {
      case 'non-blocking':
        return 'Non-blocking';
      case 'blocks-customs':
        return 'Blocks customs clearance';
      case 'blocks-delivery':
        return 'Blocks delivery';
    }
  };

  const getChangeIndicator = (indicator?: Document['changeIndicator']) => {
    if (!indicator) return null;
    
    switch (indicator) {
      case 'updated-today':
        return { icon: '▲', text: 'Updated today', type: indicator };
      case 'status-changed':
        return { icon: '⚠', text: 'Status changed', type: indicator };
      case 'no-change':
        return { icon: '⏺', text: 'No change', type: indicator };
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' +
      date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const handleDownload = (doc: Document) => {
    if (!doc.fileUrl) {
      showToast(`No uploaded file available for ${doc.name}`, 'error');
      return;
    }

    const fallbackName = `${doc.id}-${doc.name.replace(/\s+/g, '_')}`;
    const fileName = getDownloadFileName(doc.fileUrl, fallbackName);
    const link = window.document.createElement('a');
    link.href = buildForcedDownloadUrl(doc.fileUrl);
    link.download = fileName;
    link.rel = 'noopener noreferrer';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    showToast(`Download started for ${doc.name}`, 'info');
  };

  return (
    <>
      <PageHeaderStyles ref={headerRef}>
        <Title>Documents</Title>
        <Subtitle>Manage and track all shipment documentation</Subtitle>
      </PageHeaderStyles>

      {/* Full People Panel (right side) */}
      <PeoplePanel documents={filteredDocuments} offsetTop={peoplePanelOffset} onOpenMessaging={onOpenMessaging} />

      {/* Health Bar */}
      <HealthBar>
        <HealthStats>
          <HealthStat>
            <HealthLabel>Documents:</HealthLabel>
            <HealthValue>{healthStats.total} total</HealthValue>
          </HealthStat>
          <HealthStat>
            <HealthLabel>✔</HealthLabel>
            <HealthValue $variant="success">Cleared: {healthStats.cleared}</HealthValue>
          </HealthStat>
          <HealthStat>
            <HealthLabel>⏳</HealthLabel>
            <HealthValue $variant="warning">Pending: {healthStats.pending}</HealthValue>
          </HealthStat>
          <HealthStat>
            <HealthLabel>❌</HealthLabel>
            <HealthValue $variant="danger">Blocking: {healthStats.blocking}</HealthValue>
          </HealthStat>
        </HealthStats>
        {healthStats.shipmentsBlocked > 0 && (
          <HealthWarning>
            ⚠️ {healthStats.shipmentsBlocked} shipment{healthStats.shipmentsBlocked > 1 ? 's are' : ' is'} blocked by missing or incomplete documents
          </HealthWarning>
        )}
      </HealthBar>

      {/* Tabs and Filters */}
      <TabsContainerComponent>
        <Tabs>
          <Button variant="tab" data-active={activeTab === 'commercial'} onClick={() => setActiveTab('commercial')} style={{ flex: 1 }}>
            <div>Commercial Documents</div>
            <TabDescription>Invoices, bills, insurance, delivery orders</TabDescription>
          </Button>
          <Button variant="tab" data-active={activeTab === 'customs'} onClick={() => setActiveTab('customs')} style={{ flex: 1 }}>
            <div>Customs & Regulatory</div>
            <TabDescription>Permits, licenses, certificates, declarations</TabDescription>
          </Button>
        </Tabs>

        <FilterBarComponent>
          <BlockingToggle>
            <Checkbox
              checked={blockingOnly}
              onChange={(e) => setBlockingOnly(e.target.checked)}
              label="Blocking only"
            />
          </BlockingToggle>

          <FilterGroup>
            <FilterLabel>Order/Shipment:</FilterLabel>
            <SearchableSelect
              options={['All', ...orderOptions]}
              value={selectedOrder === 'all' ? 'All' : selectedOrder}
              onChange={(value) => setSelectedOrder(value === 'All' ? 'all' : value)}
              placeholder="Search orders or shipments..."
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Status:</FilterLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              items={[
                { label: 'All statuses', value: 'all' },
                { label: 'Cleared', value: 'cleared' },
                { label: 'Pending', value: 'pending' },
                { label: 'Blocking', value: 'blocking' },
              ]}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Time Risk:</FilterLabel>
            <Select
              value={selectedTimeRisk}
              onChange={(e) => setSelectedTimeRisk(e.target.value)}
              items={[
                { label: 'All documents', value: 'all' },
                { label: 'Overdue', value: 'overdue' },
                { label: 'Urgent (≤2 days)', value: 'urgent' },
                { label: 'Normal (>2 days)', value: 'normal' },
                { label: 'No deadline', value: 'no-deadline' },
              ]}
            />
          </FilterGroup>
        </FilterBarComponent>

        {/* Documents Grid */}
        <DocumentsGrid>
          {filteredDocuments.length === 0 ? (
            <EmptyState>No documents match the selected filters</EmptyState>
          ) : (
            paginatedItems.map(doc => {
              const changeInfo = getChangeIndicator(doc.changeIndicator);
              
              return (
                <CardContainer key={doc.id} variant="elevated" padding="md" hoverable>
                  <DocumentHeaderRow>
                    <DocumentTitle>
                      <DocumentId>{doc.id}</DocumentId>
                      <DocumentName>{doc.name}</DocumentName>
                    </DocumentTitle>
                  </DocumentHeaderRow>

                  <StatusRow>
                    <DocumentStatus $status={doc.status}>
                      {doc.status === 'cleared' && '✓'}
                      {doc.status === 'pending' && '⏳'}
                      {doc.status === 'blocking' && '⚠'}
                      {' '}
                      {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                    </DocumentStatus>
                    <ImpactBadge $impact={doc.impact}>
                      {getImpactIcon(doc.impact)} {getImpactText(doc.impact)}
                    </ImpactBadge>
                  </StatusRow>

                  <DocumentMeta>
                    <MetaRow>
                      <MetaLabel>Linked to:</MetaLabel>
                      <MetaValue>
                        {onOpenOrder ? (
                          <Button
                            variant="link"
                            onClick={() => onOpenOrder(doc.orderId)}
                            title="Open this order in Payments"
                            style={{ padding: 0 }}
                          >
                            {doc.orderId}
                          </Button>
                        ) : (
                          doc.orderId
                        )}
                        {doc.shipmentId && ` → ${doc.shipmentId}`}
                      </MetaValue>
                    </MetaRow>
                    <MetaRow>
                      <MetaLabel>Issued by:</MetaLabel>
                      <MetaValue>{doc.issuedBy}</MetaValue>
                    </MetaRow>
                    {doc.handledBy && (
                      <MetaRow>
                        <MetaLabel>Handled by:</MetaLabel>
                        <MetaValue>{doc.handledBy}</MetaValue>
                      </MetaRow>
                    )}
                    {doc.dueDate && (
                      <MetaRow>
                        <MetaLabel>Due:</MetaLabel>
                        <MetaValue>
                          {new Date(doc.dueDate).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </MetaValue>
                      </MetaRow>
                    )}
                  </DocumentMeta>

                  {doc.lastAction && (
                    <DocumentActivity>
                      <strong>Recent:</strong> {doc.lastAction} · {formatDateTime(doc.lastActionTime!)}
                      {changeInfo && (
                        <>
                          {' · '}
                          <ChangeIndicator $type={changeInfo.type}>
                            {changeInfo.icon} {changeInfo.text}
                          </ChangeIndicator>
                        </>
                      )}
                    </DocumentActivity>
                  )}

                  {doc.status === 'blocking' && (
                    <ImpactSignal>
                      <strong>⚠️ This document blocks:</strong>
                      <span>
                        Customs clearance → Shipment release → Payment settlement
                      </span>
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#991b1b', fontWeight: 500 }}>Action required to unblock shipment</div>
                    </ImpactSignal>
                  )}

                  <DocumentActions>
                    <Button variant="outline" size="sm" onClick={() => setSelectedDocumentForDetails(doc)}>
                      View Details
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} disabled={!doc.fileUrl}>
                      Download
                    </Button>
                  </DocumentActions>
                </CardContainer>
              );
            })
          )}
        </DocumentsGrid>
        <PaginationControls
          totalItems={filteredDocuments.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </TabsContainerComponent>
      
      {selectedDocumentForDetails && (
        <DocumentDetails
          document={selectedDocumentForDetails}
          onClose={() => setSelectedDocumentForDetails(null)}
        />
      )}
    </>
  );
};

export default DocumentsPage;
